import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircle2Icon, EyeIcon, MailIcon, ReceiptIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { MetricStat } from '../../components/admin/MetricStat';
import { AccountStateBadge } from '../../components/admin/AccountStateBadge';
import { EmptyState } from '../../components/EmptyState';
import { getCustomer } from '../../data/adminCustomers';
import { fleetChecks } from '../../data/adminPlatform';
import { getPlan, plans } from '../../data/plans';
import { formatDate, relativeTime } from '../../utils/format';
const stateStyles = {
  ok: 'text-healthy-ink',
  warning: 'text-warn-ink',
  failing: 'text-critical-ink',
  paused: 'text-paused-ink'
} as const;
export function AdminCustomerDetail() {
  const {
    customerId
  } = useParams();
  const customer = customerId ? getCustomer(customerId) : undefined;
  const [note, setNote] = useState<string | null>(null);
  if (!customer) {
    return <Panel data-id="element-479" data-mp="2i4emgxeei0w5">
        <EmptyState icon={ReceiptIcon} title="Account not found" body="That account id does not exist, or it was purged after cancellation." action={<Button variant="secondary" to="/admin/customers" data-id="element-481" data-mp="28da73wktce0s">
              Back to customers
            </Button>} data-id="element-480" data-mp="2i7ahodyv6bgw" />
      </Panel>;
  }
  const plan = getPlan(customer.planId);
  const checks = fleetChecks.filter(check => check.customerId === customer.id);
  const atCeiling = customer.servicesUsed >= plan.services;
  return <div className="space-y-5" data-id="element-482" data-mp="1phk3y6159blb">
      <Link to="/admin/customers" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted hover:text-ink" data-id="element-483" data-mp="1pgsi3mxcms88">
        <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-484" data-mp="3jutgy7swff8b" />
        All customers
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4" data-id="element-485" data-mp="wmci8l2vym7v">
        <div className="min-w-0" data-id="element-486" data-mp="rn9bmw79c7xs">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-487" data-mp="rm81cty2fph4">
            <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-488" data-mp="nl4kc44a7neb">
              {customer.company}
            </h1>
            <AccountStateBadge state={customer.state} data-id="element-489" data-mp="incwpmao6nfx" />
          </div>
          <p className="mt-1.5 font-mono text-[13px] text-ink-muted" data-id="element-490" data-mp="1fcc3sxzipj6j">
            {customer.id} · {customer.ownerName} · {customer.ownerEmail}
          </p>
          <p className="mt-1 text-xs text-ink-soft" data-id="element-491" data-mp="1bbs7fwmrqjlg">
            Signed up {formatDate(customer.signedUpAt)} · last seen{' '}
            {relativeTime(customer.lastSeenAt)} · {customer.country}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-492" data-mp="11jcoavzqjoyz">
          <Button variant="secondary" size="sm" onClick={() => setNote('Opened a read-only view of this account. The session is capped at 30 minutes and is in the audit log.')} data-id="element-493" data-mp="1z1mic8nj1y32">
            <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-494" data-mp="26janfkjx2zh2" />
            View as customer
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setNote(`Resent the most recent alert email to ${customer.ownerEmail}.`)} data-id="element-495" data-mp="2bi3zd8jk2if4">
            <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-496" data-mp="2ki0v1u4d4hjo" />
            Resend last alert
          </Button>
        </div>
      </header>

      {note && <div role="status" className="flex items-start gap-2.5 rounded-card border border-healthy-border bg-healthy-soft px-4 py-3" data-id="element-497" data-mp="2uazk1d3rua3t">
          <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-healthy" aria-hidden="true" data-id="element-498" data-mp="33av1fznx71ca" />
          <p className="text-[13px] text-healthy-ink" data-id="element-499" data-mp="38a7mqfz580cr">{note}</p>
        </div>}

      {customer.flag && <div className="rounded-card border border-warn-border bg-warn-soft px-4 py-3" data-id="element-500" data-mp="2ul5y8q9pg0dw">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-warn-ink/80" data-id="element-501" data-mp="2qkcabww9ill0">
            Flagged
          </p>
          <p className="mt-1 text-[13px] text-warn-ink" data-id="element-502" data-mp="34emppnmdd3ee">{customer.flag}</p>
        </div>}

      <Panel data-id="element-503" data-mp="2zfiiv3mx3hr8">
        <div className="grid divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x" data-id="element-504" data-mp="2zfs1u79joonf">
          <MetricStat label="Plan" value={plan.name} detail={`$${plan.monthlyPrice}/mo · ${plan.services} services`} data-id="element-505" data-mp="27m9ht84ia6ig" />
          <MetricStat label="Services used" value={`${customer.servicesUsed}/${plan.services}`} detail={atCeiling ? 'At the ceiling' : `${customer.domains} domains`} tone={atCeiling ? 'warn' : 'default'} data-id="element-506" data-mp="2lnc51o78lmuy" />
          <MetricStat label="Alerts sent" value={String(customer.alertsSent30d)} detail="Last 30 days" data-id="element-507" data-mp="3s1l32cf6zv83" />
          <MetricStat label="MRR" value={`$${customer.mrr}`} detail={customer.state === 'past_due' ? 'Payment overdue' : 'Collecting'} tone={customer.state === 'past_due' ? 'critical' : 'default'} data-id="element-508" data-mp="1it6ji463rhj7" />
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]" data-id="element-509" data-mp="1xi2qrz0c4qir">
        <Panel as="section" data-id="element-510" data-mp="1xkozw32vje33">
          <PanelHeader title="Monitors on this account" description="Read only. Changing a customer's monitors is done by asking them, not for them." level={2} data-id="element-511" data-mp="21kfukhviuecw" />
          {checks.length === 0 ? <EmptyState icon={ReceiptIcon} title="No monitors sampled" body="This account has monitors, but none are in the current fleet sample." data-id="element-512" data-mp="1sprsf0l8tcsb" /> : <ul className="divide-y divide-line" data-id="element-513" data-mp="2l9qc81etv9dn">
              {checks.map(check => <li key={check.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-3.5" data-id="element-514" data-mp="2v1t6yixi6s8x">
                  <span className="min-w-0 flex-1" data-id="element-515" data-mp="2l4oksj3nqws4">
                    <span className="block truncate font-mono text-[13px] text-ink" data-id="element-516" data-mp="2l7j1bzjjvxg9">
                      {check.domain}
                    </span>
                    <span className={`mt-1 block truncate text-xs ${stateStyles[check.state]}`} data-id="element-517" data-mp="2c631glqd3dqa">
                      {check.detail}
                    </span>
                  </span>
                  <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[11px] text-ink-muted" data-id="element-518" data-mp="22fi04qjazv3o">
                    {check.kind}
                  </span>
                  <span className="font-mono text-[11px] text-ink-soft" data-id="element-519" data-mp="1sfr24evjaxyu">
                    {relativeTime(check.lastRunAt)}
                  </span>
                </li>)}
            </ul>}
        </Panel>

        <Panel as="section" data-id="element-520" data-mp="3zhg9mnfphyq">
          <PanelHeader title="Plan actions" description="Changes here take effect on the next billing cycle." level={2} data-id="element-521" data-mp="7yq82lywz5rn" />
          <div className="space-y-3 px-5 py-5" data-id="element-522" data-mp="cw05oowginqk">
            {plans.map(option => <button key={option.id} type="button" onClick={() => setNote(option.id === customer.planId ? `${customer.company} is already on ${option.name}.` : `Queued a move to ${option.name} for ${customer.company}, effective next cycle. The customer has been emailed.`)} className={`flex w-full items-center justify-between gap-3 rounded-control border px-3.5 py-3 text-left transition-colors duration-150 ease-snap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${option.id === customer.planId ? 'border-brand bg-brand-soft' : 'border-line-strong hover:border-ink-soft hover:bg-canvas'}`} data-id="element-523" data-mp="hweguftcudst">
                <span data-id="element-524" data-mp="201o0xppefgm1">
                  <span className="block text-[13px] font-medium text-ink" data-id="element-525" data-mp="201670zq7leha">
                    {option.name}
                  </span>
                  <span className="mt-0.5 block font-mono text-[11px] text-ink-soft" data-id="element-526" data-mp="2exdq1wpvs9uz">
                    {option.services} services
                  </span>
                </span>
                <span className="font-mono text-[13px] text-ink" data-id="element-527" data-mp="2nq77o0ssvlc0">
                  ${option.monthlyPrice}
                </span>
              </button>)}

            <div className="border-t border-line pt-4" data-id="element-528" data-mp="2xp6klvklm34t">
              <Button variant="secondary" className="w-full" onClick={() => setNote(`Granted a custom allowance of 25 services to ${customer.company}. Logged as a sensitive action.`)} data-id="element-529" data-mp="2xpy78gw7wqiy">
                Grant custom allowance
              </Button>
              <p className="mt-2 text-xs leading-relaxed text-ink-soft" data-id="element-530" data-mp="32d5ypunqwdq7">
                For accounts past the 15-service ceiling. Requires a superadmin
                and shows in the audit log.
              </p>
            </div>
          </div>
        </Panel>
      </div>
    </div>;
}